/**
 * Who gets which colour: you green, allies teal, neutral grey-green, and each other player
 * a stable amber-family style (by player id, so colours never reshuffle mid-match).
 */
import type { PlayerId, PlayerInfo } from '@sa/sim';
import { COLORS, ENEMY_STYLES, PATTERN_ALLY, PATTERN_ME, PATTERN_NEUTRAL, cssRgb, type RGB } from '../theme';

export class Palette {
  readonly ownerColors = new Float32Array(9 * 4);
  private enemyStyle = new Map<PlayerId, number>();
  private allies: PlayerId[] = [];

  constructor(
    readonly me: PlayerId,
    players: readonly Pick<PlayerInfo, 'id'>[],
  ) {
    players
      .map((p) => p.id)
      .filter((id) => id !== me)
      .sort((a, b) => a - b)
      .forEach((id, i) => this.enemyStyle.set(id, i % ENEMY_STYLES.length));
    this.setAllies([]);
  }

  setAllies(allies: readonly PlayerId[]): void {
    this.allies = [...allies];
    const put = (i: number, c: RGB, pattern: number) => this.ownerColors.set([c[0], c[1], c[2], pattern], i * 4);
    put(0, COLORS.neutral, PATTERN_NEUTRAL);
    for (let id = 1; id <= 8; id++) {
      if (id === this.me) put(id, COLORS.me, PATTERN_ME);
      else if (this.allies.includes(id)) put(id, COLORS.ally, PATTERN_ALLY);
      else {
        const s = ENEMY_STYLES[this.enemyStyle.get(id) ?? 0];
        put(id, s.color, s.pattern);
      }
    }
  }

  get allyMask(): number {
    return this.allies.reduce((m, id) => m | (1 << id), 0);
  }

  /** Display colour of an owner (0 = neutral). */
  color(owner: PlayerId): RGB {
    if (owner === this.me) return COLORS.me;
    if (owner === 0) return COLORS.neutral;
    if (this.allies.includes(owner)) return COLORS.ally;
    return ENEMY_STYLES[this.enemyStyle.get(owner) ?? 0].color;
  }

  /** Fill pattern id of an owner (for UI swatches: `drawPatternSwatch`). */
  pattern(owner: PlayerId): number {
    if (owner === this.me) return PATTERN_ME;
    if (owner === 0) return PATTERN_NEUTRAL;
    if (this.allies.includes(owner)) return PATTERN_ALLY;
    return ENEMY_STYLES[this.enemyStyle.get(owner) ?? 0].pattern;
  }

  isFriendly(owner: PlayerId): boolean {
    return owner === this.me || this.allies.includes(owner);
  }
}

/**
 * A nation's territory pattern as a small swatch (Tab board, nation menu, share bar): the CPU
 * twin of the territory shader's `pattern()`, one CSS px per pattern pixel.
 */
export function drawPatternSwatch(ctx: CanvasRenderingContext2D, pattern: number, col: RGB, w = 10, h = 10): void {
  const on = (x: number, y: number): boolean => {
    const m = (a: number, b: number) => ((a % b) + b) % b;
    switch (pattern) {
      case 0: return m(y, 4) < 1;
      case 1: return m(x, 4) < 1;
      case 2: return m(x + y, 8) < 1 || m(x - y, 8) < 1;
      case 3: return m(x - y, 6) < 1;
      case 4: return m(x, 4) < 2 && m(y, 4) < 2;
      case 5: return m(y, 5) < 1 && m(x, 8) < 5;
      case 6: return m(x, 6) < 1 || m(y, 6) < 1;
      case 7: return m(x + y, 6) < 1;
      case 8: return m(x, 3) < 1 && m(y + 1.5 * Math.floor(x / 3), 3) < 1;
      default: return (x * 7 + y * 13) % 17 === 0;
    }
  };
  ctx.fillStyle = cssRgb(col, 0.18);
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = cssRgb(col, 0.95);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (on(x, y)) ctx.fillRect(x, y, 1, 1);
}
